import { useChartColors } from '../lib/accessibility'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, X, CheckCircle2, Wrench, Fuel, MapPin, ClipboardList, Image as ImageIcon } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import { api } from '../lib/api'
import { volumeUnit, litersToDisplay, inputToLiters, type MeasurementSystem } from '../lib/units'
import { PhotoManager } from '../components/shared/PhotoManager'

// Shared: the user's measurement system (imperial → gallons, metric → liters).
// The DB always stores liters; we convert at the form/display edge.
function useMeasurementSystem(): MeasurementSystem {
  const { data } = useQuery({
    queryKey: ['user-preferences'],
    queryFn: () => api.get('/me/preferences').then(r => r.data as { measurement_system?: MeasurementSystem }),
    staleTime: 5 * 60_000,
  })
  return data?.measurement_system ?? 'imperial'
}

const C = {
  bg: 'var(--color-bg)', bgRaised: 'var(--color-bg-card)', bgCard: '#1A2212',
  border: 'var(--color-border)', amber: 'var(--color-primary)', green: '#2D6A2F',
  text: 'var(--color-text)', textMuted: 'var(--color-text-muted)', textFaint: 'var(--color-text-faint)',
  hover: 'var(--color-bg-hover)', active: 'color-mix(in srgb, var(--color-primary) 12%, transparent)',
  red: '#F87171', blue: '#60A5FA',
}

type Tab = 'assets' | 'fuel' | 'maintenance' | 'location'

interface Equipment {
  id: string; name: string; type: string; make?: string; model?: string; year?: number
  serial_number?: string; fuel_type?: string; tank_capacity_liters?: number
  fuel_level_pct?: number; hours?: number; next_service_date?: string
  status: 'operational' | 'needs_service' | 'in_service' | 'retired'
  notes?: string; current_field?: string
}

interface FuelLog { id: string; equipment_name: string; date: string; liters: number; cost: number; hours_at_fill: number }
interface MaintenanceLog { id: string; equipment_name: string; date: string; type: string; cost: number; notes: string; next_service_date?: string }

const STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  operational:  { bg: '#2D6A2F22', text: '#4ADE80', label: 'Operational' },
  needs_service:{ bg: '#F5A62322', text: '#F5A623', label: 'Needs Service' },
  in_service:   { bg: '#60A5FA22', text: '#60A5FA', label: 'In Service' },
  retired:      { bg: 'var(--color-bg-hover)', text: 'var(--color-text-faint)', label: 'Retired' },
}

const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box',
  background: C.bg, border: `1px solid ${C.border}`,
  borderRadius: 8, padding: '8px 12px',
  color: C.text, fontSize: 13, outline: 'none',
}
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 500, color: C.textMuted, marginBottom: 5,
}

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_COLORS[status] ?? STATUS_COLORS.retired
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px',
      borderRadius: 6, fontSize: 11, fontWeight: 600,
      background: cfg.bg, color: cfg.text,
    }}>{cfg.label}</span>
  )
}

function FuelGauge({ pct }: { pct: number }) {
  const color = pct < 20 ? C.red : pct < 50 ? C.amber : '#4ADE80'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: 'rgba(255,255,255,0.1)', borderRadius: 3 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width 0.3s' }} />
      </div>
      <span style={{ fontSize: 11, color: C.textFaint, width: 32, textAlign: 'right' }}>{pct}%</span>
    </div>
  )
}

function AddEquipmentModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const system = useMeasurementSystem()
  const [form, setForm] = useState({
    name: '', type: 'tractor', make: '', model: '', year: '', serial_number: '',
    fuel_type: 'diesel', tank_capacity_liters: '', status: 'operational', notes: '',
  })
  const [success, setSuccess] = useState(false)
  const mutation = useMutation({
    mutationFn: (d: Record<string, unknown>) => api.post('/equipment', d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['equipment'] }); setSuccess(true); setTimeout(onClose, 1200) },
  })
  function submit(e: React.FormEvent) {
    e.preventDefault()
    mutation.mutate({ ...form, year: form.year ? parseInt(form.year) : undefined, tank_capacity_liters: form.tank_capacity_liters ? inputToLiters(parseFloat(form.tank_capacity_liters), system) : undefined })
  }
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: C.bgRaised, border: `1px solid ${C.border}`, borderRadius: 14, width: 480, maxWidth: '95vw', padding: 24, position: 'relative', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>Add Equipment</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textFaint }}><X size={18} /></button>
        </div>
        {success ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#4ADE80' }}><CheckCircle2 size={36} style={{ marginBottom: 10 }} /><div style={{ fontSize: 15, fontWeight: 600 }}>Equipment added!</div></div>
        ) : (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 2 }}>
                <label style={labelStyle}>Name *</label>
                <input required style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Main Tractor" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Type</label>
                <select style={inputStyle} value={form.type} onChange={e => set('type', e.target.value)}>
                  {['tractor','mower','pump','sprayer','truck','atv','chainsaw','blower','generator','other'].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}><label style={labelStyle}>Make</label><input style={inputStyle} value={form.make} onChange={e => set('make', e.target.value)} placeholder="John Deere" /></div>
              <div style={{ flex: 1 }}><label style={labelStyle}>Model</label><input style={inputStyle} value={form.model} onChange={e => set('model', e.target.value)} placeholder="5075E" /></div>
              <div style={{ flex: 1 }}><label style={labelStyle}>Year</label><input type="number" style={inputStyle} value={form.year} onChange={e => set('year', e.target.value)} placeholder="2020" /></div>
            </div>
            <div><label style={labelStyle}>Serial Number</label><input style={inputStyle} value={form.serial_number} onChange={e => set('serial_number', e.target.value)} /></div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Fuel Type</label>
                <select style={inputStyle} value={form.fuel_type} onChange={e => set('fuel_type', e.target.value)}>
                  {['diesel','gasoline','propane','electric','none'].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}><label style={labelStyle}>Tank Capacity ({volumeUnit(system)})</label><input type="number" style={inputStyle} value={form.tank_capacity_liters} onChange={e => set('tank_capacity_liters', e.target.value)} /></div>
            </div>
            <div>
              <label style={labelStyle}>Status</label>
              <select style={inputStyle} value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="operational">Operational</option>
                <option value="needs_service">Needs Service</option>
                <option value="in_service">In Service</option>
                <option value="retired">Retired</option>
              </select>
            </div>
            <div><label style={labelStyle}>Notes</label><textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 64, fontFamily: 'inherit' }} value={form.notes} onChange={e => set('notes', e.target.value)} /></div>
            {mutation.isError && <div style={{ fontSize: 12, color: C.red, padding: '8px 12px', background: C.red + '11', borderRadius: 8 }}>Failed to save. Please try again.</div>}
            <button type="submit" disabled={mutation.isPending} style={{ padding: '10px 0', borderRadius: 8, background: mutation.isPending ? C.green + '88' : C.green, border: 'none', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              {mutation.isPending ? 'Saving…' : 'Add Equipment'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

function LogFuelModal({ equipmentId, onClose }: { equipmentId: string; onClose: () => void }) {
  const qc = useQueryClient()
  const system = useMeasurementSystem()
  const [form, setForm] = useState({ liters: '', cost: '', hours_at_fill: '', date: new Date().toISOString().slice(0, 10) })
  const [success, setSuccess] = useState(false)
  const mutation = useMutation({
    mutationFn: (d: Record<string, unknown>) => api.post(`/equipment/${equipmentId}/fuel`, d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['equipment-fuel'] }); setSuccess(true); setTimeout(onClose, 1200) },
  })
  function submit(e: React.FormEvent) {
    e.preventDefault()
    mutation.mutate({ liters: inputToLiters(parseFloat(form.liters), system), cost: parseFloat(form.cost), hours_at_fill: form.hours_at_fill ? parseFloat(form.hours_at_fill) : undefined, date: form.date })
  }
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: C.bgRaised, border: `1px solid ${C.border}`, borderRadius: 14, width: 400, maxWidth: '95vw', padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>Log Fuel</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textFaint }}><X size={18} /></button>
        </div>
        {success ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#4ADE80' }}><CheckCircle2 size={36} style={{ marginBottom: 10 }} /><div style={{ fontSize: 15, fontWeight: 600 }}>Fuel logged!</div></div>
        ) : (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div><label style={labelStyle}>Date</label><input type="date" style={inputStyle} value={form.date} onChange={e => set('date', e.target.value)} /></div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}><label style={labelStyle}>Amount ({volumeUnit(system)}) *</label><input required type="number" step="0.1" style={inputStyle} value={form.liters} onChange={e => set('liters', e.target.value)} /></div>
              <div style={{ flex: 1 }}><label style={labelStyle}>Cost ($) *</label><input required type="number" step="0.01" style={inputStyle} value={form.cost} onChange={e => set('cost', e.target.value)} /></div>
            </div>
            <div><label style={labelStyle}>Hours at Fill</label><input type="number" style={inputStyle} value={form.hours_at_fill} onChange={e => set('hours_at_fill', e.target.value)} /></div>
            {mutation.isError && <div style={{ fontSize: 12, color: C.red, padding: '8px 12px', background: C.red + '11', borderRadius: 8 }}>Failed to save.</div>}
            <button type="submit" disabled={mutation.isPending} style={{ padding: '10px 0', borderRadius: 8, background: C.green, border: 'none', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              {mutation.isPending ? 'Saving…' : 'Log Fuel'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

function LogServiceModal({ equipmentId, onClose }: { equipmentId: string; onClose: () => void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({ type: 'oil_change', cost: '', notes: '', date: new Date().toISOString().slice(0, 10), next_service_date: '' })
  const [success, setSuccess] = useState(false)
  const mutation = useMutation({
    mutationFn: (d: Record<string, unknown>) => api.post(`/equipment/${equipmentId}/maintenance`, d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['equipment-maintenance'] }); setSuccess(true); setTimeout(onClose, 1200) },
  })
  function submit(e: React.FormEvent) {
    e.preventDefault()
    mutation.mutate({ ...form, cost: form.cost ? parseFloat(form.cost) : undefined })
  }
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: C.bgRaised, border: `1px solid ${C.border}`, borderRadius: 14, width: 420, maxWidth: '95vw', padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>Log Service</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textFaint }}><X size={18} /></button>
        </div>
        {success ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#4ADE80' }}><CheckCircle2 size={36} style={{ marginBottom: 10 }} /><div style={{ fontSize: 15, fontWeight: 600 }}>Service logged!</div></div>
        ) : (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div><label style={labelStyle}>Service Type</label>
              <select style={inputStyle} value={form.type} onChange={e => set('type', e.target.value)}>
                {['oil_change','filter_change','tire_rotation','blade_sharpening','belt_replacement','inspection','repair','other'].map(t => <option key={t} value={t}>{t.replace(/_/g,' ')}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}><label style={labelStyle}>Date</label><input type="date" style={inputStyle} value={form.date} onChange={e => set('date', e.target.value)} /></div>
              <div style={{ flex: 1 }}><label style={labelStyle}>Cost ($)</label><input type="number" step="0.01" style={inputStyle} value={form.cost} onChange={e => set('cost', e.target.value)} /></div>
            </div>
            <div><label style={labelStyle}>Next Service Date</label><input type="date" style={inputStyle} value={form.next_service_date} onChange={e => set('next_service_date', e.target.value)} /></div>
            <div><label style={labelStyle}>Notes</label><textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 64, fontFamily: 'inherit' }} value={form.notes} onChange={e => set('notes', e.target.value)} /></div>
            {mutation.isError && <div style={{ fontSize: 12, color: C.red, padding: '8px 12px', background: C.red + '11', borderRadius: 8 }}>Failed to save.</div>}
            <button type="submit" disabled={mutation.isPending} style={{ padding: '10px 0', borderRadius: 8, background: C.green, border: 'none', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              {mutation.isPending ? 'Saving…' : 'Log Service'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

export function EquipmentPage() {
  const chartC = useChartColors()
  const system = useMeasurementSystem()
  const [tab, setTab] = useState<Tab>('assets')
  const [showAdd, setShowAdd] = useState(false)
  const [showFuel, setShowFuel] = useState<string | null>(null)
  const [showService, setShowService] = useState<string | null>(null)
  const [showPhotos, setShowPhotos] = useState<{ id: string; name: string } | null>(null)

  const { data: equipment = [], isLoading, isError, refetch } = useQuery<Equipment[]>({
    queryKey: ['equipment'],
    queryFn: () => api.get('/equipment').then(r => r.data?.equipment ?? r.data ?? []),
  })
  const { data: fuelLogs = [], isLoading: fuelLoading } = useQuery<FuelLog[]>({
    queryKey: ['equipment-fuel'],
    queryFn: () => api.get('/equipment/fuel').then(r => r.data?.fuel_logs ?? r.data ?? []).catch(() => []),
    enabled: tab === 'fuel',
  })
  const { data: maintenanceLogs = [], isLoading: maintLoading } = useQuery<MaintenanceLog[]>({
    queryKey: ['equipment-maintenance'],
    queryFn: () => api.get('/equipment/maintenance').then(r => r.data?.maintenance_logs ?? r.data ?? []).catch(() => []),
    enabled: tab === 'maintenance',
  })

  const tabs: { id: Tab; label: string; icon: typeof Wrench }[] = [
    { id: 'assets', label: 'Assets', icon: Wrench },
    { id: 'fuel', label: 'Fuel Log', icon: Fuel },
    { id: 'maintenance', label: 'Maintenance', icon: ClipboardList },
    { id: 'location', label: 'Location', icon: MapPin },
  ]

  const tabStyle = (active: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 7,
    fontSize: 13, fontWeight: active ? 600 : 400, cursor: 'pointer',
    background: active ? C.active : 'transparent',
    color: active ? C.amber : C.textMuted, border: 'none',
  })

  return (
    <>
      {showAdd && <AddEquipmentModal onClose={() => setShowAdd(false)} />}
      {showFuel && <LogFuelModal equipmentId={showFuel} onClose={() => setShowFuel(null)} />}
      {showService && <LogServiceModal equipmentId={showService} onClose={() => setShowService(null)} />}
      {showPhotos && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setShowPhotos(null) }}>
          <div style={{ background: C.bgRaised, border: `1px solid ${C.border}`, borderRadius: 14, width: 520, maxWidth: '95vw', padding: 24, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>{showPhotos.name} — photos</h2>
              <button onClick={() => setShowPhotos(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textFaint }}><X size={18} /></button>
            </div>
            <PhotoManager resourceType="equipment" resourceId={showPhotos.id} label="Equipment photos" />
          </div>
        </div>
      )}

      <main id="main-content" style={{ padding: '28px 32px', minHeight: '100%', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.text }}>Equipment</h1>
            <p style={{ margin: '3px 0 0', fontSize: 13, color: C.textMuted }}>Fleet assets, fuel tracking, and service history</p>
          </div>
          <button onClick={() => setShowAdd(true)} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', borderRadius: 9, background: C.green, border: 'none', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            <Plus size={16} /> Add Equipment
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: C.bgCard, borderRadius: 10, padding: 4, border: `1px solid ${C.border}`, width: 'fit-content' }}>
          {tabs.map(t => {
            const Icon = t.icon
            return <button key={t.id} style={tabStyle(tab === t.id)} onClick={() => setTab(t.id)}><Icon size={14} />{t.label}</button>
          })}
        </div>

        {/* Assets Tab */}
        {tab === 'assets' && (
          <div>
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '60px 0', color: C.textFaint }}>Loading equipment…</div>
            ) : isError ? (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <p style={{ color: C.red, marginBottom: 12 }}>Failed to load equipment</p>
                <button onClick={() => refetch()} style={{ padding: '8px 16px', borderRadius: 8, background: C.bgCard, border: `1px solid ${C.border}`, color: C.text, cursor: 'pointer' }}>Retry</button>
              </div>
            ) : equipment.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <Wrench size={40} style={{ color: C.textFaint, opacity: 0.4, marginBottom: 12 }} />
                <p style={{ margin: 0, color: C.textFaint }}>No equipment added yet</p>
                <p style={{ margin: '6px 0 0', fontSize: 12, color: C.textFaint }}>Click "Add Equipment" to track your fleet</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
                {equipment.map(eq => (
                  <div key={eq.id} style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{eq.name}</div>
                        <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>{eq.make} {eq.model} {eq.year ? `· ${eq.year}` : ''}</div>
                      </div>
                      <StatusBadge status={eq.status} />
                    </div>
                    <div style={{ fontSize: 11, color: C.textFaint, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Fuel Level</div>
                    <FuelGauge pct={eq.fuel_level_pct ?? 0} />
                    <div style={{ display: 'flex', gap: 16, marginTop: 12 }}>
                      {eq.hours !== undefined && (
                        <div><div style={{ fontSize: 11, color: C.textFaint }}>Hours</div><div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{eq.hours.toLocaleString()}</div></div>
                      )}
                      {eq.next_service_date && (
                        <div><div style={{ fontSize: 11, color: C.textFaint }}>Next Service</div><div style={{ fontSize: 13, color: C.amber }}>{new Date(eq.next_service_date).toLocaleDateString([], { month: 'short', day: 'numeric' })}</div></div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
                      <button onClick={() => setShowFuel(eq.id)} style={{ flex: 1, padding: '6px 0', borderRadius: 7, background: 'transparent', border: `1px solid ${C.border}`, color: C.textMuted, fontSize: 12, cursor: 'pointer' }}>Log Fuel</button>
                      <button onClick={() => setShowService(eq.id)} style={{ flex: 1, padding: '6px 0', borderRadius: 7, background: 'transparent', border: `1px solid ${C.border}`, color: C.textMuted, fontSize: 12, cursor: 'pointer' }}>Log Service</button>
                      <button onClick={() => setShowPhotos({ id: eq.id, name: eq.name })} title="Photos" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px 10px', borderRadius: 7, background: 'transparent', border: `1px solid ${C.border}`, color: C.textMuted, fontSize: 12, cursor: 'pointer' }}><ImageIcon size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Fuel Log Tab */}
        {tab === 'fuel' && (
          <>
          {fuelLogs.length > 0 && (() => {
            const now = new Date()
            const months: { label: string; cost: number; liters: number }[] = []
            for (let i = 5; i >= 0; i--) {
              const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
              months.push({ label: d.toLocaleDateString([], { month: 'short' }), cost: 0, liters: 0 })
            }
            fuelLogs.forEach(f => {
              const d = new Date(f.date)
              const key = d.toLocaleDateString([], { month: 'short' })
              const slot = months.find(m => m.label === key)
              if (slot) { slot.cost += f.cost; slot.liters += f.liters }
            })
            const totalCost = fuelLogs.reduce((s, f) => s + f.cost, 0)
            return (
              <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: '16px 20px', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Fuel size={14} style={{ color: C.amber }} />
                    <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Fuel Cost — Last 6 Months</span>
                  </div>
                  <span style={{ fontSize: 12, color: C.textFaint }}>Total: ${totalCost.toFixed(2)}</span>
                </div>
                <ResponsiveContainer width="100%" height={120}>
                  <BarChart data={months} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                    <XAxis dataKey="label" tick={{ fill: chartC.faint, fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: chartC.faint, fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} width={40} />
                    <Tooltip contentStyle={{ background: C.bgRaised, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12, color: C.text }} formatter={(v: number, name: string, entry: { payload?: { liters?: number } }) => [`$${v.toFixed(2)} (${litersToDisplay(entry.payload?.liters ?? 0, system).toFixed(1)}${volumeUnit(system)})`, 'Fuel cost']} labelStyle={{ color: C.textMuted }} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                    <Bar dataKey="cost" radius={[4, 4, 0, 0]} maxBarSize={36}>
                      {months.map((_, i) => <Cell key={i} fill={chartC.accent} fillOpacity={i === months.length - 1 ? 1 : 0.4} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )
          })()}
          <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>Recent Fuel Events</span>
              <button onClick={() => equipment.length > 0 ? setShowFuel(equipment[0].id) : undefined} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 7, background: C.green, border: 'none', color: '#fff', fontSize: 13, cursor: 'pointer' }}><Plus size={14} /> Log Fuel</button>
            </div>
            {fuelLoading ? (
              <div style={{ padding: '40px 0', textAlign: 'center', color: C.textFaint }}>Loading…</div>
            ) : fuelLogs.length === 0 ? (
              <div style={{ padding: '48px 0', textAlign: 'center', color: C.textFaint }}>No fuel events logged yet</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {['Equipment','Date','Liters','Cost','Hours at Fill'].map(h => <th key={h} style={{ padding: '10px 18px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: C.textFaint, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {fuelLogs.map(f => (
                    <tr key={f.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                      <td style={{ padding: '12px 18px', color: C.text, fontWeight: 500 }}>{f.equipment_name}</td>
                      <td style={{ padding: '12px 18px', color: C.textMuted }}>{new Date(f.date).toLocaleDateString()}</td>
                      <td style={{ padding: '12px 18px', color: C.text }}>{f.liters}L</td>
                      <td style={{ padding: '12px 18px', color: C.text }}>${f.cost.toFixed(2)}</td>
                      <td style={{ padding: '12px 18px', color: C.textMuted }}>{f.hours_at_fill ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          </>
        )}

        {/* Maintenance Tab */}
        {tab === 'maintenance' && (
          <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>Service History</span>
              <button onClick={() => equipment.length > 0 ? setShowService(equipment[0].id) : undefined} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 7, background: C.green, border: 'none', color: '#fff', fontSize: 13, cursor: 'pointer' }}><Plus size={14} /> Log Service</button>
            </div>
            {maintLoading ? (
              <div style={{ padding: '40px 0', textAlign: 'center', color: C.textFaint }}>Loading…</div>
            ) : maintenanceLogs.length === 0 ? (
              <div style={{ padding: '48px 0', textAlign: 'center', color: C.textFaint }}>No maintenance records yet</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {['Equipment','Date','Service Type','Cost','Next Service'].map(h => <th key={h} style={{ padding: '10px 18px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: C.textFaint, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {maintenanceLogs.map(m => (
                    <tr key={m.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                      <td style={{ padding: '12px 18px', color: C.text, fontWeight: 500 }}>{m.equipment_name}</td>
                      <td style={{ padding: '12px 18px', color: C.textMuted }}>{new Date(m.date).toLocaleDateString()}</td>
                      <td style={{ padding: '12px 18px', color: C.text }}>{m.type.replace(/_/g,' ')}</td>
                      <td style={{ padding: '12px 18px', color: C.text }}>{m.cost ? `$${m.cost.toFixed(2)}` : '—'}</td>
                      <td style={{ padding: '12px 18px', color: m.next_service_date ? C.amber : C.textFaint }}>{m.next_service_date ? new Date(m.next_service_date).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Location Tab */}
        {tab === 'location' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '60px 0', color: C.textFaint }}>Loading…</div>
            ) : equipment.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 0', color: C.textFaint }}>No equipment to show</div>
            ) : (
              equipment.map(eq => (
                <div key={eq.id} style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <MapPin size={16} style={{ color: C.amber, flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{eq.name}</div>
                      <div style={{ fontSize: 12, color: C.textMuted }}>{eq.current_field ?? 'Unassigned'}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => api.post(`/equipment/${eq.id}/location`, { field: 'unassigned' })}
                    style={{ padding: '6px 14px', borderRadius: 7, background: 'transparent', border: `1px solid ${C.border}`, color: C.textMuted, fontSize: 12, cursor: 'pointer' }}>
                    Reassign
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </>
  )
}
